import { useEffect, useMemo, useState } from "react";
import type { HistoryStore, Sample } from "./positionHistory";
import {
  buildRouteProjection,
  type RawRouteGeometry,
  type RouteProjection,
} from "./routeProjection";
import type { Vehicle } from "./useVehicleStream";
import "./Headways.css";

// Cruise speed used elsewhere in the repo for converting an inter-train
// distance gap into an approximate time headway.
const CRUISE_MPS = 13.4;
// Cap bar width at this headway so a single huge gap doesn't blow out the
// horizontal scale of the rest of the panel.
const MAX_BAR_SEC = 20 * 60;
// Sparkline window and bucket size.
const SPARK_WINDOW_SEC = 30 * 60;
const SPARK_BUCKET_SEC = 60;

type PanelSpec = {
  id: string;
  routeId: string;
  routeLabel: string;
  directionLabel: string;
  shortLabel: string;
  headsignMatch: string[];
  /** Target headway band (min, max) in seconds, drawn as a faint band. */
  targetMinSec: number;
  targetMaxSec: number;
};

const PANELS: PanelSpec[] = [
  {
    id: "1-north",
    routeId: "40_100479",
    routeLabel: "1 Line",
    directionLabel: "Northbound (toward Lynnwood)",
    shortLabel: "1 N",
    headsignMatch: ["lynnwood"],
    targetMinSec: 8 * 60,
    targetMaxSec: 12 * 60,
  },
  {
    id: "1-south",
    routeId: "40_100479",
    routeLabel: "1 Line",
    directionLabel: "Southbound (toward Federal Way / Angle Lake)",
    shortLabel: "1 S",
    headsignMatch: ["federal way", "angle lake"],
    targetMinSec: 8 * 60,
    targetMaxSec: 12 * 60,
  },
  {
    id: "2-east",
    routeId: "40_2LINE",
    routeLabel: "2 Line",
    directionLabel: "Eastbound (toward Redmond)",
    shortLabel: "2 E",
    headsignMatch: ["redmond"],
    targetMinSec: 10 * 60,
    targetMaxSec: 15 * 60,
  },
  {
    id: "2-west",
    routeId: "40_2LINE",
    routeLabel: "2 Line",
    directionLabel: "Westbound (toward Lynnwood)",
    shortLabel: "2 W",
    headsignMatch: ["lynnwood"],
    targetMinSec: 10 * 60,
    targetMaxSec: 15 * 60,
  },
];

function matchesPanel(headsign: string | undefined, panel: PanelSpec): boolean {
  if (!headsign) return false;
  const h = headsign.toLowerCase();
  return panel.headsignMatch.some((m) => h.includes(m));
}

// In-service classification — mirror the backend status string conventions
// used elsewhere (the SSE feed reports "in_progress" for trains actively
// serving a trip; deadheads/layovers report otherwise or omit status).
function isInService(v: Vehicle): boolean {
  if (!v.status) return true; // tolerate missing status — assume in-service
  const s = v.status.toLowerCase();
  return s === "in_progress" || s === "in progress" || s === "in_transit_to";
}

type Props = {
  vehicles: Vehicle[];
  history: HistoryStore;
  historyVersion: number;
  connected: boolean;
  historical?: boolean;
};

type Pair = {
  aId: string;
  bId: string | null;
  /** Distance gap in meters between consecutive trains (null if single). */
  distM: number | null;
  /** Implied time headway (seconds) — distM / CRUISE_MPS. null if single. */
  gapSec: number | null;
};

type PanelDerived = {
  panel: PanelSpec;
  projection: RouteProjection | null;
  pairs: Pair[];
  /** Per-minute median headway samples for the sparkline (oldest → newest). */
  spark: { ts: number; medianSec: number | null }[];
  stats: {
    count: number;
    meanSec: number | null;
    medianSec: number | null;
    minSec: number | null;
    maxSec: number | null;
    bunchingPairs: number;
  };
};

export function Headways({
  vehicles,
  history,
  historyVersion,
  connected,
  historical = false,
}: Props) {
  const [projections, setProjections] = useState<RouteProjection[] | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/routes/geometry");
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = (await res.json()) as RawRouteGeometry[];
        if (cancelled) return;
        const projs = data
          .map(buildRouteProjection)
          .filter((p) => p.lengthM > 0);
        setProjections(projs);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Derive the per-panel headway analysis. Memoized on historyVersion so
  // when live we recompute exactly when the SSE store mutates, and when
  // scrubbed-historical the snapshot stays frozen between version bumps.
  const derived: PanelDerived[] = useMemo(() => {
    if (!projections) return [];
    const projByRoute = new Map(projections.map((p) => [p.routeId, p]));

    return PANELS.map((panel) => {
      const projection = projByRoute.get(panel.routeId) ?? null;
      if (!projection) {
        return {
          panel,
          projection: null,
          pairs: [],
          spark: [],
          stats: emptyStats(),
        };
      }

      // --- Live-frame pairs (the current snapshot, used for bars + stats) ---
      const placed = vehicles
        .filter(
          (v) =>
            v.route_id === panel.routeId &&
            isInService(v) &&
            matchesPanel(v.trip_headsign, panel),
        )
        .map((v) => {
          const distM = projection.project(v.lat, v.lon);
          return distM === null ? null : { v, distM };
        })
        .filter((x): x is { v: Vehicle; distM: number } => x !== null)
        .sort((a, b) => a.distM - b.distM);

      const pairs: Pair[] = [];
      if (placed.length === 1) {
        pairs.push({
          aId: placed[0].v.vehicle_id,
          bId: null,
          distM: null,
          gapSec: null,
        });
      } else {
        for (let i = 0; i < placed.length - 1; i++) {
          const a = placed[i];
          const b = placed[i + 1];
          const distM = b.distM - a.distM;
          pairs.push({
            aId: a.v.vehicle_id,
            bId: b.v.vehicle_id,
            distM,
            gapSec: distM / CRUISE_MPS,
          });
        }
      }

      const gaps = pairs
        .map((p) => p.gapSec)
        .filter((g): g is number => g !== null && Number.isFinite(g));
      const stats = computeStats(gaps);

      // --- Sparkline: walk positionHistory, minute-bucket median headways ---
      const spark = computeSparkline(history, projection, panel);

      return { panel, projection, pairs, spark, stats };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    });
  }, [historyVersion, vehicles, projections]);

  // Global summary strip data — recompute alongside per-panel derived.
  const summary = useMemo(() => {
    let trainCount = 0;
    const allGaps: number[] = [];
    let bunching = 0;
    for (const d of derived) {
      // Count trains: pairs.length+1 if there are real pairs; or 1 for the
      // single-train placeholder; or 0 when empty.
      if (d.pairs.length === 0) {
        trainCount += 0;
      } else if (d.pairs.length === 1 && d.pairs[0].bId === null) {
        trainCount += 1;
      } else {
        trainCount += d.pairs.length + 1;
      }
      for (const p of d.pairs) {
        if (p.gapSec !== null) {
          allGaps.push(p.gapSec);
          if (p.gapSec < 90) bunching += 1;
        }
      }
    }
    const meanAll =
      allGaps.length === 0
        ? null
        : allGaps.reduce((s, g) => s + g, 0) / allGaps.length;
    return { trainCount, meanAll, bunching };
  }, [derived]);

  if (error) {
    return (
      <div className="headways-empty">
        Couldn't load route geometry: {error}
      </div>
    );
  }
  if (!projections) {
    return <div className="headways-empty">Loading route geometry…</div>;
  }

  return (
    <div className="headways">
      <div className="headways-summary">
        <div className="headways-summary-cell">
          <div className="headways-summary-value">{summary.trainCount}</div>
          <div className="headways-summary-label">trains in service</div>
        </div>
        <div className="headways-summary-cell">
          <div className="headways-summary-value">
            {summary.meanAll === null ? "—" : formatMmSs(summary.meanAll)}
          </div>
          <div className="headways-summary-label">avg headway (all)</div>
        </div>
        <div
          className={`headways-summary-cell ${
            summary.bunching > 0 ? "alert" : ""
          }`}
        >
          <div className="headways-summary-value">
            {summary.bunching > 0 && <span className="headways-dot" />}
            {summary.bunching}
          </div>
          <div className="headways-summary-label">
            bunching {summary.bunching === 1 ? "alert" : "alerts"}
          </div>
        </div>
        <div className="headways-summary-spacer" />
        <div
          className={`headways-status ${
            historical ? "history" : connected ? "ok" : "wait"
          }`}
        >
          {historical
            ? "◐ history"
            : connected
              ? "● live"
              : "○ waiting…"}
        </div>
      </div>

      <div className="headways-grid">
        {derived.map((d) => (
          <HeadwayPanel key={d.panel.id} d={d} />
        ))}
      </div>
    </div>
  );
}

function HeadwayPanel({ d }: { d: PanelDerived }) {
  const { panel, projection, pairs, spark, stats } = d;
  const swatch = projection?.color ?? "#888";
  const hasTrains = pairs.length > 0;
  const hasGaps = pairs.some((p) => p.gapSec !== null);

  return (
    <div className="headways-panel">
      <div className="headways-panel-header">
        <span
          className="headways-bullet"
          style={{ background: swatch }}
          aria-hidden
        />
        <span className="headways-panel-route">{panel.routeLabel}</span>
        <span className="headways-panel-sep">·</span>
        <span className="headways-panel-dir">{panel.directionLabel}</span>
      </div>

      <div className="headways-bars-wrap">
        {!hasTrains && (
          <div className="headways-empty-inline">no trains in service</div>
        )}
        {hasTrains && !hasGaps && (
          <div className="headways-single">
            single train in service: {pairs[0].aId}
          </div>
        )}
        {hasTrains && hasGaps && (
          <HeadwayBars pairs={pairs} panel={panel} />
        )}
      </div>

      <div className="headways-stats">
        <Stat label="mean" value={stats.meanSec} />
        <Stat label="median" value={stats.medianSec} />
        <Stat label="min" value={stats.minSec} />
        <Stat label="max" value={stats.maxSec} />
        <Stat label="gaps" value={stats.count} raw />
      </div>

      <div className="headways-spark-wrap">
        <Sparkline samples={spark} panel={panel} />
        <div className="headways-spark-caption">
          median headway · last 30 min
        </div>
      </div>
    </div>
  );
}

function HeadwayBars({ pairs, panel }: { pairs: Pair[]; panel: PanelSpec }) {
  // Target band drawn behind the bars at the panel's target headway range.
  const bandLeftPct = (Math.min(panel.targetMinSec, MAX_BAR_SEC) / MAX_BAR_SEC) * 100;
  const bandRightPct =
    (Math.min(panel.targetMaxSec, MAX_BAR_SEC) / MAX_BAR_SEC) * 100;
  return (
    <div className="headways-bars">
      <div
        className="headways-bars-band"
        style={{
          left: `${bandLeftPct}%`,
          width: `${Math.max(bandRightPct - bandLeftPct, 0)}%`,
        }}
        aria-hidden
      />
      {pairs.map((p, i) => {
        if (p.gapSec === null) return null;
        const widthPct = Math.max(
          1.5,
          (Math.min(p.gapSec, MAX_BAR_SEC) / MAX_BAR_SEC) * 100,
        );
        const cls = barClass(p.gapSec);
        const overflow = p.gapSec > MAX_BAR_SEC;
        return (
          <div className="headways-bar-row" key={`${p.aId}-${p.bId}-${i}`}>
            <div className="headways-bar-track">
              <div
                className={`headways-bar ${cls}`}
                style={{ width: `${widthPct}%` }}
              >
                <span className="headways-bar-label">
                  {formatMmSs(p.gapSec)}
                  {overflow && "+"}
                </span>
              </div>
            </div>
            <div className="headways-bar-meta" title={`${p.aId} → ${p.bId}`}>
              {shortId(p.aId)} → {shortId(p.bId)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Stat({
  label,
  value,
  raw = false,
}: {
  label: string;
  value: number | null;
  raw?: boolean;
}) {
  return (
    <div className="headways-stat">
      <div className="headways-stat-value">
        {value === null ? "—" : raw ? value.toString() : formatMmSs(value)}
      </div>
      <div className="headways-stat-label">{label}</div>
    </div>
  );
}

function Sparkline({
  samples,
  panel,
}: {
  samples: { ts: number; medianSec: number | null }[];
  panel: PanelSpec;
}) {
  const W = 220;
  const H = 44;
  const PAD_X = 2;
  const PAD_Y = 4;

  const present = samples.filter(
    (s): s is { ts: number; medianSec: number } => s.medianSec !== null,
  );

  // Y range: 0 → max(observed, target band top * 1.2), capped at MAX_BAR_SEC.
  const observedMax = present.reduce(
    (m, s) => Math.max(m, s.medianSec),
    panel.targetMaxSec * 1.2,
  );
  const yMaxSec = Math.min(MAX_BAR_SEC, Math.max(observedMax, 60));

  const xFor = (i: number) => {
    if (samples.length <= 1) return PAD_X;
    return PAD_X + (i / (samples.length - 1)) * (W - PAD_X * 2);
  };
  const yFor = (sec: number) => {
    const clamped = Math.min(sec, yMaxSec);
    return H - PAD_Y - (clamped / yMaxSec) * (H - PAD_Y * 2);
  };

  // Build path with breaks for null buckets.
  let d = "";
  let pen = false;
  for (let i = 0; i < samples.length; i++) {
    const s = samples[i];
    if (s.medianSec === null) {
      pen = false;
      continue;
    }
    const x = xFor(i);
    const y = yFor(s.medianSec);
    d += `${pen ? "L" : "M"}${x.toFixed(1)} ${y.toFixed(1)} `;
    pen = true;
  }

  const bandTop = yFor(Math.min(panel.targetMaxSec, yMaxSec));
  const bandBot = yFor(Math.min(panel.targetMinSec, yMaxSec));
  const bandY = Math.min(bandTop, bandBot);
  const bandH = Math.max(Math.abs(bandBot - bandTop), 1);

  const hasData = present.length > 0;

  return (
    <svg
      className="headways-spark"
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      role="img"
      aria-label="Median headway over the last 30 minutes"
    >
      <rect
        className="headways-spark-band"
        x={0}
        y={bandY}
        width={W}
        height={bandH}
      />
      {hasData ? (
        <path className="headways-spark-line" d={d.trim()} />
      ) : (
        <text
          className="headways-spark-empty"
          x={W / 2}
          y={H / 2 + 3}
          textAnchor="middle"
        >
          no history yet
        </text>
      )}
    </svg>
  );
}

// --- helpers ------------------------------------------------------------

function emptyStats() {
  return {
    count: 0,
    meanSec: null,
    medianSec: null,
    minSec: null,
    maxSec: null,
    bunchingPairs: 0,
  };
}

function computeStats(gaps: number[]) {
  if (gaps.length === 0) return emptyStats();
  const sorted = [...gaps].sort((a, b) => a - b);
  const sum = sorted.reduce((s, g) => s + g, 0);
  const mean = sum / sorted.length;
  const median =
    sorted.length % 2 === 0
      ? (sorted[sorted.length / 2 - 1] + sorted[sorted.length / 2]) / 2
      : sorted[(sorted.length - 1) / 2];
  return {
    count: sorted.length,
    meanSec: mean,
    medianSec: median,
    minSec: sorted[0],
    maxSec: sorted[sorted.length - 1],
    bunchingPairs: sorted.filter((g) => g < 90).length,
  };
}

function barClass(gapSec: number): string {
  if (gapSec < 90) return "bar-red";
  if (gapSec < 4 * 60) return "bar-amber";
  if (gapSec <= 12 * 60) return "bar-green";
  return "bar-blue";
}

function formatMmSs(sec: number): string {
  if (!Number.isFinite(sec) || sec < 0) return "—";
  const m = Math.floor(sec / 60);
  const s = Math.round(sec - m * 60);
  // Handle the rare rounding case where seconds rolls to 60.
  if (s === 60) return `${m + 1}:00`;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function shortId(id: string | null): string {
  if (!id) return "—";
  // Vehicle IDs from OBA/Sound Transit can be long; show the trailing chunk
  // which is the unique-per-train portion.
  return id.length <= 6 ? id : id.slice(-4);
}

// computeSparkline walks the rolling 30-minute history, buckets samples into
// minute windows, and at each window computes the median headway implied by
// the in-direction trains' along-route ordering. Returns oldest → newest.
function computeSparkline(
  history: HistoryStore,
  proj: RouteProjection,
  panel: PanelSpec,
): { ts: number; medianSec: number | null }[] {
  // Anchor the buckets to the freshest sample we have in the store, so the
  // sparkline lines up with the displayed snapshot whether we're live or
  // viewing a frozen historical anchor.
  let latest = 0;
  for (const entry of history.byVehicle.values()) {
    if (entry.routeId !== proj.routeId) continue;
    const last = entry.samples[entry.samples.length - 1];
    if (last && last.ts > latest) latest = last.ts;
  }
  if (latest === 0) {
    // Fall back to wall clock; we'll just emit empty buckets.
    latest = Math.floor(Date.now() / 1000);
  }
  const endTs = Math.floor(latest / SPARK_BUCKET_SEC) * SPARK_BUCKET_SEC;
  const startTs = endTs - SPARK_WINDOW_SEC + SPARK_BUCKET_SEC;
  const bucketCount = Math.floor(SPARK_WINDOW_SEC / SPARK_BUCKET_SEC);

  // Pre-filter relevant vehicles' samples once.
  type V = { id: string; samples: Sample[] };
  const relevant: V[] = [];
  for (const entry of history.byVehicle.values()) {
    if (entry.routeId !== proj.routeId) continue;
    const matched = entry.samples.filter((s) =>
      matchesPanel(s.tripHeadsign, panel),
    );
    if (matched.length > 0) {
      relevant.push({ id: entry.vehicleId, samples: matched });
    }
  }

  const out: { ts: number; medianSec: number | null }[] = [];
  for (let b = 0; b < bucketCount; b++) {
    const bEnd = startTs + (b + 1) * SPARK_BUCKET_SEC;
    const bStart = bEnd - SPARK_BUCKET_SEC;

    // For each vehicle take the latest sample within the window (if any).
    const placed: number[] = [];
    for (const v of relevant) {
      let chosen: Sample | null = null;
      // samples are already ascending; walk forward and keep the last one
      // with ts < bEnd. Cheap enough for our 30-minute buffer sizes.
      for (const s of v.samples) {
        if (s.ts >= bEnd) break;
        if (s.ts >= bStart - SPARK_BUCKET_SEC * 2) {
          // Look back up to 2 buckets to tolerate sparse SSE updates without
          // dropping a vehicle from the bucket.
          chosen = s;
        }
      }
      if (!chosen) continue;
      const dist = proj.project(chosen.lat, chosen.lon);
      if (dist === null) continue;
      placed.push(dist);
    }

    placed.sort((a, b2) => a - b2);
    if (placed.length < 2) {
      out.push({ ts: bEnd, medianSec: null });
      continue;
    }
    const gaps: number[] = [];
    for (let i = 0; i < placed.length - 1; i++) {
      gaps.push((placed[i + 1] - placed[i]) / CRUISE_MPS);
    }
    gaps.sort((a, b2) => a - b2);
    const median =
      gaps.length % 2 === 0
        ? (gaps[gaps.length / 2 - 1] + gaps[gaps.length / 2]) / 2
        : gaps[(gaps.length - 1) / 2];
    out.push({ ts: bEnd, medianSec: median });
  }

  return out;
}
